/**
 * Writing saves (architecture §4.1): debounced 1,500 ms after a store change, immediately on `visibilitychange → hidden`,
 * `pagehide` and after reward grants; rotation `save.0 → save.1 → save.2` on a successful save ≥ 10 min after the last
 * rotation; size budget 1 MB (warn at 800 KB). Read-only and in-memory sessions never write `marg.save.*`.
 */
import { STORAGE_KEYS, type SaveState } from '../../shared';
import { wrapEnvelope } from './envelope';
import { StorageFullError, type KeyValueStorage } from './storage';

export const SAVE_DEBOUNCE_MS = 1500;
export const ROTATE_EVERY_MS = 10 * 60 * 1000;
export const SAVE_WARN_BYTES = 800 * 1024;
export const SAVE_MAX_BYTES = 1024 * 1024;

export interface SaveResult { ok: boolean; bytes: number; rotated: boolean; error?: 'storage-full' | 'too-big' | 'read-only' | 'unknown'; warning?: string }

export interface TimerApi { set(fn: () => void, ms: number): unknown; clear(handle: unknown): void }

export interface PersistOptions {
  /** null = nothing is ever written (memory session). */
  storage: KeyValueStorage | null;
  /** Current state supplier. */
  getState: () => SaveState;
  /** Wall clock (already offset by `?wall=` / `timeTravel`). */
  nowWall: () => number;
  /** Called after each attempt (the app emits `app/saved` or a warning toast). */
  onResult?: (r: SaveResult, reason: string) => void;
  timers?: TimerApi;
  debounceMs?: number;
}

const defaultTimers: TimerApi = { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };

export class SaveWriter {
  private readOnly = false;
  private pending: unknown = null;
  private pendingReason = '';
  private lastRotationAt: number;
  private readonly timers: TimerApi;
  private readonly debounceMs: number;
  /** Writes performed (tests, `__marg.getState()`). */
  writes = 0;
  lastResult: SaveResult | null = null;

  private storage: KeyValueStorage | null;

  constructor(private readonly opts: PersistOptions) {
    this.storage = opts.storage;
    this.timers = opts.timers ?? defaultTimers;
    this.debounceMs = opts.debounceMs ?? SAVE_DEBOUNCE_MS;
    this.lastRotationAt = this.readSavedAt(STORAGE_KEYS.save[1]) ?? 0;
  }

  /** In-memory session: nothing is written. */
  get memory(): boolean { return this.storage === null; }
  /** Switches to an in-memory session for good (`__marg.setSave`, `loadFixture`): nothing is written afterwards. */
  toMemory(): void { this.cancel(); this.storage = null; }
  /** Read-only: a slice was written by a newer build (architecture §4.2). */
  setReadOnly(on: boolean): void { this.readOnly = on; if (on) this.cancel(); }
  get isReadOnly(): boolean { return this.readOnly; }

  /** Debounced save. */
  schedule(reason: string): void {
    if (this.memory || this.readOnly) return;
    this.pendingReason = reason;
    if (this.pending !== null) this.timers.clear(this.pending);
    this.pending = this.timers.set(() => { this.pending = null; this.flush(this.pendingReason); }, this.debounceMs);
  }

  cancel(): void {
    if (this.pending !== null) { this.timers.clear(this.pending); this.pending = null; }
  }

  /** True while a debounced save is waiting. */
  get hasPending(): boolean { return this.pending !== null; }

  /** Writes now (also used for hidden/pagehide/after grants). */
  flush(reason: string): SaveResult {
    this.cancel();
    const storage = this.storage;
    if (!storage) return this.report({ ok: false, bytes: 0, rotated: false }, reason);
    if (this.readOnly) return this.report({ ok: false, bytes: 0, rotated: false, error: 'read-only' }, reason);
    const now = this.opts.nowWall();
    const state = this.opts.getState();
    const data: SaveState = { ...state, lastActiveAt: Math.max(state.lastActiveAt, now) };
    const raw = JSON.stringify(wrapEnvelope(data, now));
    const bytes = raw.length * 2;
    if (bytes > SAVE_MAX_BYTES) return this.report({ ok: false, bytes, rotated: false, error: 'too-big' }, reason);
    let rotated = false;
    try {
      if (now - this.lastRotationAt >= ROTATE_EVERY_MS) {
        const s0 = storage.getItem(STORAGE_KEYS.save[0]);
        if (s0 !== null) {
          const s1 = storage.getItem(STORAGE_KEYS.save[1]);
          if (s1 !== null) storage.setItem(STORAGE_KEYS.save[2], s1);
          storage.setItem(STORAGE_KEYS.save[1], s0);
          rotated = true;
        }
        this.lastRotationAt = now;
      }
      storage.setItem(STORAGE_KEYS.save[0], raw);
    } catch (e) {
      return this.report({ ok: false, bytes, rotated, error: e instanceof StorageFullError ? 'storage-full' : 'unknown' }, reason);
    }
    this.writes++;
    const warning = bytes > SAVE_WARN_BYTES ? `save is ${(bytes / 1024).toFixed(0)} KB (warn at 800 KB)` : undefined;
    return this.report({ ok: true, bytes, rotated, warning }, reason);
  }

  private report(r: SaveResult, reason: string): SaveResult {
    this.lastResult = r;
    this.opts.onResult?.(r, reason);
    return r;
  }

  private readSavedAt(key: string): number | null {
    const raw = this.opts.storage?.getItem(key);
    if (!raw) return null;
    try { const v = (JSON.parse(raw) as { savedAt?: unknown }).savedAt; return typeof v === 'number' ? v : null; } catch { return null; }
  }
}
